import { useCallback, useEffect, useRef } from 'react';

interface BeforeRemoveEvent {
  preventDefault: () => void;
}

interface NavigationLike {
  addListener: (
    event: 'beforeRemove',
    listener: (e: BeforeRemoveEvent) => void,
  ) => () => void;
  goBack: () => void;
}

/**
 * Intercepts stack pops (gesture / hardware back) so the screen can run a
 * guarded close handler (e.g. unsaved-changes prompt) instead of dismissing immediately.
 *
 * Call `markCloseConfirmedAndGoBack` when actually leaving after save/discard.
 *
 * Call `registerCloseHandler` from a child (unsaved-changes flow) and/or from the
 * screen when the effective close target changes (e.g. SKU picker vs editor).
 */
export const useInterceptNavigationBeforeRemove = (
  navigation: NavigationLike,
) => {
  const isCloseConfirmedRef = useRef(false);
  const closeHandlerRef = useRef<() => void>(() => {
    navigation.goBack();
  });

  useEffect(() => {
    return navigation.addListener('beforeRemove', (event) => {
      if (isCloseConfirmedRef.current) {
        return;
      }
      event.preventDefault();
      closeHandlerRef.current();
    });
  }, [navigation]);

  const markCloseConfirmedAndGoBack = useCallback(() => {
    isCloseConfirmedRef.current = true;
    navigation.goBack();
  }, [navigation]);

  const registerCloseHandler = useCallback((handler: () => void) => {
    closeHandlerRef.current = handler;
  }, []);

  const invokeCloseHandler = useCallback(() => {
    closeHandlerRef.current();
  }, []);

  return {
    invokeCloseHandler,
    markCloseConfirmedAndGoBack,
    registerCloseHandler,
  };
};
